import { MapNoAddress } from "./map-no-address.component";
import type {
	MapCenter,
	MapNavigationControlPosition,
	PharmacyMarker,
} from "./pharmacy-map.types";
import { PharmacyMapLazy } from "./pharmacy-map-lazy.component";

/**
 * Renders the map for a resolved postal-code centroid.
 *
 * `center` is always supplied by the caller (VendorFilterMapWrapper's own
 * `usePostalCodeCenter()`, threaded down through MobileFullscreenMapOverlay
 * too) rather than resolved here — a second, independent
 * `usePostalCodeCenter()` call used to live in this component, which let the
 * map show pharmacies from its own fetch while a caller-side race (MKP-2054:
 * a delivery-method switch's navigation can abort an in-flight
 * `fetcher.load()`) left the radius control's `center` — and therefore its
 * `disabled` state — permanently stuck, even though the map itself worked.
 * One source of truth keeps the radius control and the map in sync.
 */
interface PharmacyMapPanelProps {
	center: MapCenter | null;
	pharmacyMarkers?: PharmacyMarker[];
	selectedPharmacyId?: number | null;
	onPharmacyMarkerClick?: (pharmacyId: number) => void;
	onDismissPreview?: () => void;
	interactive?: boolean;
	navigationControlPosition?: MapNavigationControlPosition;
	isLoading?: boolean;
	hasVendorResults?: boolean;
	onEnterAddress?: () => void;
}

export function PharmacyMapPanel({
	center,
	pharmacyMarkers,
	selectedPharmacyId,
	onPharmacyMarkerClick,
	onDismissPreview,
	interactive,
	navigationControlPosition,
	isLoading = false,
	hasVendorResults = true,
	onEnterAddress,
}: PharmacyMapPanelProps) {
	// No address yet (and not just loading one): prompt the user to enter one.
	if (!center && !isLoading) {
		return <MapNoAddress onEnterAddress={onEnterAddress} />;
	}

	return (
		<PharmacyMapLazy
			center={center}
			pharmacyMarkers={pharmacyMarkers}
			selectedPharmacyId={selectedPharmacyId}
			onPharmacyMarkerClick={onPharmacyMarkerClick}
			onDismissPreview={onDismissPreview}
			interactive={interactive}
			navigationControlPosition={navigationControlPosition}
			isLoading={isLoading}
			hasVendorResults={hasVendorResults}
		/>
	);
}
